import {
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type PointerEvent as ReactPointerEvent,
} from "react";
import type { GraphEdge, GraphNode, OntologyItem, SourceReference } from "../types";
import { Icon } from "./Icon";
import {
  getDetailDrawerDragOffset,
  shouldDismissDetailDrawer,
} from "./detailDrawerDrag";

interface DetailDragState {
  moved: boolean;
  offset: number;
  pointerId: number;
  startY: number;
  startedAt: number;
}

const INITIAL_CONNECTION_COUNT = 5;

interface DetailConnection {
  edge: GraphEdge;
  node: GraphNode;
}

interface DetailConnectionGroup {
  connections: DetailConnection[];
  edgeTypeId: string;
}

interface DetailDrawerProps {
  edges: GraphEdge[];
  edgeTypes: OntologyItem[];
  node: GraphNode | null;
  nodeById: Map<string, GraphNode>;
  nodeTypes: OntologyItem[];
  origins?: OntologyItem[];
  onClose: () => void;
  onSelect: (nodeId: string) => void;
}

function formatDate(value: string) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat("ko-KR", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(date);
}

function formatScore(value: number) {
  if (value <= 1) return `${Math.round(value * 100)}%`;
  if (value <= 5) return `${value.toFixed(value % 1 === 0 ? 0 : 1)} / 5`;
  return `${Math.round(value)}%`;
}

function getSourceLabel(source: SourceReference) {
  if (typeof source === "string") return source;
  return source.title ?? source.label ?? source.path ?? source.url ?? "출처";
}

function getSourceUrl(source: SourceReference) {
  if (typeof source === "string") {
    return /^https?:\/\//.test(source) ? source : null;
  }
  return source.url && /^https?:\/\//.test(source.url) ? source.url : null;
}

export function DetailDrawer({
  edges,
  edgeTypes,
  node,
  nodeById,
  nodeTypes,
  origins = [],
  onClose,
  onSelect,
}: DetailDrawerProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<DetailDragState | null>(null);
  const ignoreClickRef = useRef(false);
  const [dragOffset, setDragOffset] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [isPeeked, setIsPeeked] = useState(false);

  useLayoutEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = 0;
    setIsPeeked(false);
  }, [node?.id]);

  const resetDrag = () => {
    dragRef.current = null;
    setDragOffset(0);
    setIsDragging(false);
  };

  const finishDrag = (event: ReactPointerEvent<HTMLButtonElement>) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;

    const elapsedMs = performance.now() - drag.startedAt;
    const shouldPeek = shouldDismissDetailDrawer(drag.offset, elapsedMs);
    ignoreClickRef.current = drag.moved;
    resetDrag();

    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }

    if (shouldPeek) setIsPeeked(true);
  };

  const handleDragPointerDown = (event: ReactPointerEvent<HTMLButtonElement>) => {
    if (event.pointerType === "mouse" && event.button !== 0) return;

    event.preventDefault();
    event.currentTarget.setPointerCapture(event.pointerId);
    dragRef.current = {
      moved: false,
      offset: 0,
      pointerId: event.pointerId,
      startY: event.clientY,
      startedAt: performance.now(),
    };
    ignoreClickRef.current = false;
    setIsDragging(true);
  };

  const handleDragPointerMove = (event: ReactPointerEvent<HTMLButtonElement>) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;

    drag.moved ||= Math.abs(event.clientY - drag.startY) > 4;
    drag.offset = getDetailDrawerDragOffset(drag.startY, event.clientY);
    setDragOffset(drag.offset);
  };

  const handleDragPointerCancel = (event: ReactPointerEvent<HTMLButtonElement>) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;

    ignoreClickRef.current = drag.moved;
    resetDrag();
  };

  const handleDragClick = () => {
    if (ignoreClickRef.current) {
      ignoreClickRef.current = false;
      return;
    }

    setIsPeeked((peeked) => !peeked);
  };

  if (!node) return null;

  const nodeType = nodeTypes.find((type) => type.id === node.type);
  const origin = origins.find((item) => item.id === node.origin);
  const primaryTopic = node.topics[0];
  const edgeTypeById = new Map(edgeTypes.map((type) => [type.id, type] as const));
  const connections = edges
    .filter((edge) => edge.source === node.id || edge.target === node.id)
    .map((edge) => ({
      edge,
      node: nodeById.get(edge.source === node.id ? edge.target : edge.source),
    }))
    .filter((connection): connection is DetailConnection => Boolean(connection.node))
    .sort((left, right) => right.edge.weight - left.edge.weight);
  const groupConnections = (items: DetailConnection[]): DetailConnectionGroup[] =>
    Array.from(
      items.reduce((groups, connection) => {
        const group = groups.get(connection.edge.type) ?? [];
        group.push(connection);
        groups.set(connection.edge.type, group);
        return groups;
      }, new Map<string, DetailConnection[]>()).entries(),
    ).map(([edgeTypeId, groupedConnections]) => ({
      connections: groupedConnections,
      edgeTypeId,
    }));
  const remainingConnections = connections.slice(INITIAL_CONNECTION_COUNT);
  const visibleConnectionGroups = groupConnections(connections.slice(0, INITIAL_CONNECTION_COUNT));
  const remainingConnectionGroups = groupConnections(remainingConnections);

  const renderEdgeTypeLabel = (edgeTypeId: string) => {
    const edgeType = edgeTypeById.get(edgeTypeId);
    return (
      <span className="connection-group-label">
        {edgeType ? (
          <span
            aria-hidden="true"
            className="detail-badge-dot"
            style={{ "--badge-color": edgeType.color } as CSSProperties}
          />
        ) : null}
        {edgeType?.label ?? edgeTypeId}
      </span>
    );
  };

  const renderConnection = ({ edge, node: connectedNode }: DetailConnection) => (
    <button
      className="connection-row"
      key={edge.id}
      onClick={() => onSelect(connectedNode.id)}
      type="button"
    >
      <span className="connection-copy">
        <strong>{connectedNode.title}</strong>
        <small className="connection-reason">{edge.reason}</small>
      </span>
      <Icon name="arrow-right" size={16} />
    </button>
  );

  const renderConnectionGroups = (groups: DetailConnectionGroup[]) =>
    groups.map(({ connections: groupedConnections, edgeTypeId }) => (
      <details className="connection-group" key={edgeTypeId} open>
        <summary>
          <Icon className="connection-group-chevron" name="chevron" size={14} />
          {renderEdgeTypeLabel(edgeTypeId)}
          <span className="connection-group-count">{groupedConnections.length}</span>
        </summary>
        <div className="connection-group-list">
          {groupedConnections.map(renderConnection)}
        </div>
      </details>
    ));

  return (
    <aside
      className={`detail-drawer${isDragging ? " is-dragging" : ""}${isPeeked ? " is-peeked" : ""}`}
      aria-labelledby="detail-title"
      aria-live="polite"
      style={{ "--detail-drag-offset": `${dragOffset}px` } as CSSProperties}
    >
      <header className="detail-header">
        <button
          aria-expanded={!isPeeked}
          aria-label={isPeeked ? "상세 패널 펼치기" : "상세 패널 접기"}
          className="detail-drag-handle"
          onClick={handleDragClick}
          onLostPointerCapture={handleDragPointerCancel}
          onPointerCancel={handleDragPointerCancel}
          onPointerDown={handleDragPointerDown}
          onPointerMove={handleDragPointerMove}
          onPointerUp={finishDrag}
          title={isPeeked ? "상세 패널 펼치기" : "끌어 내려 접기"}
          type="button"
        >
          <span aria-hidden="true">
            {isPeeked ? <Icon name="chevron" size={18} /> : null}
          </span>
        </button>
        <div className="detail-heading-actions">
          <div className="detail-badges" aria-label="노드 분류">
            <span className="detail-badge">
              <span
                aria-hidden="true"
                className="detail-badge-dot"
                style={{ "--badge-color": nodeType?.color ?? "#5c8ef5" } as CSSProperties}
              />
              {nodeType?.label ?? node.type}
            </span>
            {primaryTopic ? <span className="detail-badge">{primaryTopic}</span> : null}
            {origin ? <span className="detail-badge">{origin.label}</span> : null}
          </div>
          <button
            aria-label="상세 패널 닫기"
            className="detail-close"
            onClick={onClose}
            type="button"
          >
            <Icon name="close" size={16} />
          </button>
        </div>
        <h2 id="detail-title">{node.title}</h2>
        <div className="detail-meta" aria-label="노드 신뢰도와 중요도">
          <span>신뢰도 {formatScore(node.confidence)}</span>
          <span>중요도 {formatScore(node.importance)}</span>
          <span>직접 연결 {connections.length}</span>
        </div>
      </header>

      <div className="detail-scroll" ref={scrollRef}>
        <section className="detail-section">
          <h3>핵심 주장</h3>
          <p className="detail-lead">{node.claim}</p>
        </section>

        <section className="detail-section">
          <h3>왜 중요한가</h3>
          <p>{node.why}</p>
        </section>

        {node.caveat ? (
          <section className="detail-section caveat-section">
            <h3>주의할 점</h3>
            <p>{node.caveat}</p>
          </section>
        ) : null}

        {node.tags.length > 0 ? (
          <section className="detail-section tag-section">
            <h3>라벨</h3>
            <ul aria-label="노드 라벨">
              {node.tags.map((tag) => (
                <li key={tag}>{tag}</li>
              ))}
            </ul>
          </section>
        ) : null}

        <section className="detail-section connection-section">
          <div className="detail-section-heading">
            <h3>연결된 노드</h3>
            <span>{connections.length}</span>
          </div>
          {connections.length > 0 ? (
            <div className="connection-list">
              {renderConnectionGroups(visibleConnectionGroups)}
              {remainingConnections.length > 0 ? (
                <details className="remaining-connections">
                  <summary>
                    <Icon className="remaining-connections-chevron" name="chevron" size={15} />
                    <span>나머지 연결 {remainingConnections.length}개 보기</span>
                  </summary>
                  <div className="connection-list">
                    {renderConnectionGroups(remainingConnectionGroups)}
                  </div>
                </details>
              ) : null}
            </div>
          ) : (
            <p className="connection-empty">표시할 직접 연결이 없습니다.</p>
          )}
        </section>

        {node.sourceRefs && node.sourceRefs.length > 0 ? (
          <section className="detail-section source-section">
            <h3>출처</h3>
            <ul>
              {node.sourceRefs.map((source, index) => {
                const url = getSourceUrl(source);
                return (
                  <li key={`${getSourceLabel(source)}-${index}`}>
                    <Icon name="source" size={14} />
                    {url ? (
                      <a href={url} rel="noreferrer" target="_blank">
                        {getSourceLabel(source)}
                      </a>
                    ) : (
                      <span>{getSourceLabel(source)}</span>
                    )}
                  </li>
                );
              })}
            </ul>
          </section>
        ) : null}
      </div>

      <footer className="detail-footer">
        <span>생성 · {formatDate(node.createdAt)}</span>
        <span>업데이트 · {formatDate(node.updatedAt)}</span>
        <span
          className="detail-origin"
          style={{ "--origin-color": origin?.color ?? "#8d9290" } as CSSProperties}
          title={origin?.description ?? node.origin}
        >
          {origin?.label ?? node.origin}
        </span>
      </footer>
    </aside>
  );
}
